import { CalendarDays, Compass, Home, Map, Bookmark } from "lucide-react";
import { useNavigate, useLocation } from "react-router-dom";
import { useLang } from "@/lib/i18n";
import { useSaved } from "@/lib/SavedContext";
import { useExplore } from "@/lib/ExploreContext";

// Primary mobile navigation — a deliberate five-stop visitor journey.
// Keep this order and terminology aligned with the bilingual site language.
const tabs = [
  { id: "explore", en: "Explore", es: "Explorar", icon: Home, to: "/" },
  { id: "experiences", en: "Experiences", es: "Experiencias", icon: Compass, to: "/" },
  { id: "events", en: "Events", es: "Eventos", icon: CalendarDays, to: "/events" },
  { id: "map", en: "Map", es: "Mapa", icon: Map, to: "/map" },
  { id: "saved", en: "My List", es: "Mi lista", icon: Bookmark, to: "/saved" },
];

export default function BottomNav() {
  const navigate = useNavigate();
  const location = useLocation();
  const { t, lang } = useLang();
  const { saved } = useSaved();
  const { activeChip, setActiveChip, clearFilters } = useExplore();
  const savedCount = saved?.size || 0;
  const experiencesActive =
    (location.pathname === "/" && activeChip === "Experiences") ||
    location.pathname.startsWith("/experience/");

  const goTo = (tab) => {
    if (tab.id === "experiences") {
      // Clear search/category filters first, so selecting Experiences always
      // opens its curated browse view instead of stale filtered results.
      clearFilters();
      setActiveChip("Experiences");
      navigate("/");
      window.scrollTo({ top: 0, behavior: "auto" });
      return;
    }
    if (tab.id === "explore") {
      clearFilters();
      navigate("/");
      window.scrollTo({ top: 0, behavior: "auto" });
      return;
    }
    navigate(tab.to);
  };

  return (
    <nav
      data-tour="nav"
      className="fixed inset-x-0 bottom-0 z-[60] border-t border-black/[0.10] bg-white/95 shadow-[0_-3px_14px_rgba(0,0,0,0.04)] backdrop-blur-xl dark:border-white/10 dark:bg-[#171719]/95 sm:hidden"
      style={{ paddingBottom: "max(6px, env(safe-area-inset-bottom, 0px))" }}
      aria-label={t("nav.primary")}
    >
      <div className="mx-auto grid w-full max-w-[640px] grid-cols-5">
        {tabs.map((tab) => {
          const Icon = tab.icon;
          const isActive = tab.id === "experiences"
            ? experiencesActive
            : tab.id === "explore"
              ? location.pathname === "/" && !experiencesActive
              : tab.id === "saved"
                ? ["/planner", "/saved"].includes(location.pathname)
                : location.pathname === tab.to;
          const label = lang === "es" ? tab.es : tab.en;
          return (
            <button
              key={tab.id}
              type="button"
              onClick={() => goTo(tab)}
              aria-current={isActive ? "page" : undefined}
              aria-label={label}
              className={`relative flex min-h-[61px] min-w-0 flex-col items-center justify-center gap-1 px-0.5 text-[10px] leading-none transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#E30613] focus-visible:ring-inset sm:text-[11px] ${
                isActive ? "font-bold text-[#E30613] dark:text-white" : "font-medium text-[#707070] hover:text-[#111] dark:text-white/60 dark:hover:text-white"
              }`}
            >
              <span aria-hidden="true" className={`absolute inset-x-0 top-0 mx-auto h-[2px] w-6 bg-[#E30613] transition-opacity ${isActive ? "opacity-100" : "opacity-0"}`} />
              <span className="relative inline-flex">
                <Icon className="h-[23px] w-[23px]" strokeWidth={isActive ? 2.2 : 1.85} aria-hidden="true" />
                {tab.id === "saved" && savedCount > 0 && (
                  <span className="absolute -right-2.5 -top-1.5 min-w-[16px] h-4 rounded-full bg-[#E30613] px-1 text-center text-[9px] font-bold leading-4 text-white">
                    {savedCount > 99 ? "99+" : savedCount}
                  </span>
                )}
              </span>
              <span className="max-w-full whitespace-nowrap tracking-[-0.025em]">{label}</span>
            </button>
          );
        })}
      </div>
    </nav>
  );
}
